/**
 * Base → market price lookup
 * -----------------------------------------------------------------------------
 * The Athos index is built in the shop's base currency and exposes no
 * market-aware price fields, so its price facets and range filters are always
 * base-currency values. Range filters must keep being *sent* in base currency —
 * that's what the index holds — but every amount shown to a shopper has to be a
 * real market price, or it won't line up with the product cards.
 *
 * The localisation pass already resolves both prices for each product, so it
 * records those pairs here and the price filter reads its labels straight out of
 * them. No conversion rate is involved: a displayed price is always a price some
 * product on the page actually has. Pairs accumulate as further pages load.
 */

interface PricePair {
  base: number;
  market: number;
}

let pricePairs: PricePair[] = []

/** Record real base/market price pairs resolved while localising results. */
function recordMarketPrices(pairs: Array<[number, number]>): void {
  const merged = new Map(pricePairs.map((pair) => [pair.base, pair]))

  for (const [base, market] of pairs) {
    if (!(base > 0) || !(market > 0)) continue
    merged.set(base, { base, market })
  }

  pricePairs = [...merged.values()].sort((first, second) => first.base - second.base)
}

function nearestPair(base: number): PricePair | null {
  if (!pricePairs.length) return null

  return pricePairs.reduce((closest, pair) => {
    return Math.abs(pair.base - base) < Math.abs(closest.base - base) ? pair : closest
  })
}

/**
 * The market price recorded against a base amount — exact when that price was seen
 * in the results, otherwise the closest one that was. Null on the base market,
 * where no localisation runs and base values are already the right ones.
 */
function getMarketPrice(base: number): number | null {
  return nearestPair(base)?.market ?? null
}

/**
 * Snap a base amount onto a price a product actually has, so a slider thumb, its
 * label and the range sent to the index can never describe different amounts.
 */
function snapToRecordedPrice(base: number): number {
  return nearestPair(base)?.base ?? base
}

export { recordMarketPrices, getMarketPrice, snapToRecordedPrice }
